/**
 * API Service Layer
 * Handles all communication with the Cloudflare Workers backend
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.bandi.comuni.xyz';

class ApiService {
  private api: AxiosInstance;
  private isRefreshing = false;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401 && !this.isRefreshing) {
          this.isRefreshing = true;
          // Token expired or invalid
          const refreshed = await this.refreshToken();
          this.isRefreshing = false;
          
          if (refreshed) {
            // Retry original request
            return this.api.request(error.config!);
          } else {
            // Redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
        
        if (error.response?.status === 403) {
          toast.error('Non hai i permessi per questa operazione');
        } else if (error.response?.status === 500) {
          toast.error('Errore del server. Riprova più tardi');
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(username: string, password: string) {
    const response = await this.api.post('/auth/login', { username, password });
    if (response.data.success) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  }

  async logout() {
    await this.api.post('/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  async verifyAuth() {
    const response = await this.api.get('/auth/verify');
    return response.data;
  }

  async refreshToken() {
    // For now, don't attempt refresh - just return false to redirect to login
    // This prevents the infinite loop when no valid session exists
    return false;
  }

  // Tenders endpoints
  async getTenders(params?: {
    page?: number;
    limit?: number;
    search?: string;
    stato?: string;
    tipo?: string;
    comune?: string;
    minAmount?: string;
    maxAmount?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const response = await this.api.get('/api/tenders', { params });
    return response.data;
  }

  async getTenderById(id: string) {
    const response = await this.api.get(`/api/tenders/${id}`);
    return response.data;
  }

  async getTenderDetails(id: string) {
    const response = await this.api.get(`/api/tenders/${id}/details`);
    return response.data;
  }

  async exportTenders(format: 'csv' | 'excel', filters?: any) {
    const response = await this.api.post('/api/tenders/export', {
      format,
      filters,
    }, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Companies endpoints
  async getCompanies(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }) {
    const response = await this.api.get('/api/companies', { params });
    return response.data;
  }

  async getCompanyById(id: string) {
    const response = await this.api.get(`/api/companies/${id}`);
    return response.data;
  }

  async getCompanyTenders(id: string) {
    const response = await this.api.get(`/api/companies/${id}/tenders`);
    return response.data;
  }

  async getTopCompanies(limit: number = 10) {
    const response = await this.api.get('/api/companies/top', { 
      params: { limit } 
    });
    return response.data.data;
  }

  // Portals endpoints
  async getPortals() {
    const response = await this.api.get('/api/portals');
    return response.data;
  }

  async getPortalById(id: string) {
    const response = await this.api.get(`/api/portals/${id}`);
    return response.data;
  }

  async createPortal(data: any) {
    const response = await this.api.post('/api/admin/portals', data);
    return response.data;
  }

  async updatePortal(id: string, data: any) {
    const response = await this.api.put(`/api/admin/portals/${id}`, data);
    return response.data;
  }

  async deletePortal(id: string) {
    const response = await this.api.delete(`/api/admin/portals/${id}`);
    return response.data;
  }

  async syncPortal(id: string) {
    const response = await this.api.post(`/api/admin/portals/${id}/sync`);
    return response.data;
  }

  // Stats endpoints
  async getStats(comuneId?: string) {
    const params = comuneId && comuneId !== 'all' ? { comune: comuneId } : {};
    const response = await this.api.get('/api/stats', { params });
    return response.data.data;
  }

  async getAnalytics(type: string) {
    const response = await this.api.get(`/api/stats/analytics/${type}`);
    return response.data.data;
  }

  async getComuniStats() {
    const response = await this.api.get('/api/stats/by-comune');
    return response.data.data;
  }

  async getFilterOptions() {
    const response = await this.api.get('/api/filters');
    return response.data.data;
  }

  // Public endpoints (no auth required)
  async getPublicTenders(limit: number = 20) {
    const response = await axios.get(`${API_URL}/api/public/tenders`, {
      params: { limit },
    });
    return response.data;
  }

  async getPublicStats() {
    const response = await axios.get(`${API_URL}/api/public/stats`);
    return response.data;
  }

  async getPublicPortals() {
    const response = await axios.get(`${API_URL}/api/public/portals`);
    return response.data;
  }

  // Admin endpoints
  async getUsers() {
    const response = await this.api.get('/api/admin/users');
    return response.data;
  }

  async createUser(data: any) {
    const response = await this.api.post('/api/admin/users', data);
    return response.data;
  }

  async updateUser(id: string, data: any) {
    const response = await this.api.put(`/api/admin/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string) {
    const response = await this.api.delete(`/api/admin/users/${id}`);
    return response.data;
  }

  async getSyncLogs() {
    const response = await this.api.get('/api/admin/sync-logs');
    return response.data;
  }

  async triggerFullSync() {
    const response = await this.api.post('/api/admin/sync/full');
    return response.data;
  }
}

export default new ApiService();